import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import {
  pick,
  unwrapListItems,
  withProcurementMitraId,
} from '../../../Components/QuantityTable/utils/quantityDeliveryUtils';
import { CARGO_RATIO_LABELS } from './lpgShared';

const pickNamedValue = value => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'object') {
    return (
      value.name ??
      value.Name ??
      value.location?.name ??
      value.location?.Name ??
      value.Location?.name ??
      value.Location?.Name ??
      value.ratioName ??
      value.RatioName ??
      null
    );
  }
  return value;
};

const unwrapQuantityInformations = deliveryItem => {
  if (!deliveryItem) return [];

  const nested =
    deliveryItem?.quantityInformations ??
    deliveryItem?.QuantityInformations ??
    [];

  return Array.isArray(nested) ? nested : [];
};

export const fetchQuantityInformationTargets = async (
  procurementId,
  quantityDeliveryTermId
) => {
  const token = getAuthToken();
  if (!token || !procurementId || !quantityDeliveryTermId) return [];

  const response = await fetch(
    `${api.API_URL}/quantity-delivery?ProcurementId=${procurementId}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to load quantity delivery (${response.status})`);
  }

  const result = await response.json();
  const deliveryItems = unwrapListItems(result.data ?? result);

  const matchedDeliveryItem = deliveryItems.find(
    item => String(pick(item, 'id', 'Id')) === String(quantityDeliveryTermId)
  );

  const quantityInformations = matchedDeliveryItem
    ? unwrapQuantityInformations(matchedDeliveryItem)
    : deliveryItems.flatMap(unwrapQuantityInformations).filter(item => {
        const termId = pick(
          item,
          'quantityDeliveryTermId',
          'QuantityDeliveryTermId'
        );
        return String(termId) === String(quantityDeliveryTermId);
      });

  return quantityInformations
    .map(item => ({
      quantityInformationFk: pick(item, 'id', 'Id'),
      quantity: pick(item, 'quantity', 'Quantity'),
    }))
    .filter(item => item.quantityInformationFk)
    .sort((a, b) => Number(a.quantity) - Number(b.quantity));
};

export const fetchCargoRatioOptions = async () => {
  const token = getAuthToken();
  if (!token) return [];

  try {
    const response = await fetch(
      `${api.API_URL}/ratio-lpg?PageNumber=1&PageSize=100`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) return [];

    const result = await response.json();
    const items = unwrapListItems(result.data ?? result);

    return items
      .map(item => ({
        id: pick(item, 'id', 'Id'),
        label:
          pick(item, 'ratioName', 'RatioName') ??
          pickNamedValue(pick(item, 'ratioLpg', 'RatioLpg')),
      }))
      .filter(option => option.id && option.label)
      .filter(option => CARGO_RATIO_LABELS.includes(option.label));
  } catch {
    return [];
  }
};

export const fetchAllCargoRatioSelections = async (
  quantityInformationFks = null,
  procurementMitraId = null
) => {
  const token = getAuthToken();
  if (!token) return [];

  const fks = Array.isArray(quantityInformationFks)
    ? [
        ...new Set(
          quantityInformationFks
            .filter(fk => fk !== null && fk !== undefined && fk !== '')
            .map(String)
        ),
      ]
    : quantityInformationFks !== null &&
        quantityInformationFks !== undefined &&
        quantityInformationFks !== ''
      ? [String(quantityInformationFks)]
      : [];

  const fetchByQuery = async query => {
    // be#456: Alpha/AlphaImprovement = nilai bidder `procurementMitraId`.
    const response = await fetch(
      withProcurementMitraId(
        `${api.API_URL}/quantity-information-cargo-ratio${query}`,
        procurementMitraId
      ),
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to load cargo ratio selections (${response.status})`
      );
    }

    const result = await response.json();
    return unwrapListItems(result.data ?? result);
  };

  if (!fks.length) {
    return fetchByQuery('');
  }

  const lists = await Promise.all(
    fks.map(fk =>
      fetchByQuery(`?QuantityInformationFK=${encodeURIComponent(fk)}`).catch(
        () => []
      )
    )
  );

  const byId = new Map();
  lists.flat().forEach(item => {
    const id = pick(item, 'id', 'Id');
    if (id === null || id === undefined || id === '') return;
    byId.set(String(id), item);
  });

  return byId.size ? [...byId.values()] : lists.flat();
};
